import { useMyStoryReview } from "@/lib/api/mentor-story-review";
import { useMentorStoriesEnabled } from "@/lib/hooks/use-mentor-stories";
import { useCurrentUser } from "@/lib/api/user";
import { isMentorIdentity } from "@/lib/utils/system-roles";
import {
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
} from "@tanstack/react-router";
import { useMemo } from "react";
import { RecordPageLoader } from "./record-page-loader";
import { UserRecordShell } from "./user-record-shell";
import {
  getSelfViewTabs,
  isMentorOnlySelfPath,
  isSelfViewTabId,
  SELF_TAB_ROUTES,
  selfTabFromPath,
} from "./user-record-tabs";

export function UserRecordSelfLayout() {
  const { data: user, isPending } = useCurrentUser();
  const location = useLocation();
  const navigate = useNavigate();
  const isMentor = isMentorIdentity(user?.systemRoles);
  const storiesEnabled = useMentorStoriesEnabled();
  const { data: storyReview } = useMyStoryReview(isMentor && storiesEnabled);
  const tabs = useMemo(() => getSelfViewTabs(user), [user]);
  const storyNeedsAttention =
    storyReview?.status === "in_review" ||
    storyReview?.status === "changes_requested";
  const activeTabId = selfTabFromPath(location.pathname);

  if (isPending || !user) {
    return <RecordPageLoader />;
  }

  if (
    isMentorOnlySelfPath(location.pathname) &&
    !isMentorIdentity(user.systemRoles)
  ) {
    return <Navigate to="/profile/account" />;
  }

  return (
    <UserRecordShell
      user={user}
      surface="self"
      tabs={tabs}
      activeTabId={activeTabId}
      badges={storyNeedsAttention ? { "first-moments": 1 } : undefined}
      onTabSelect={(id) => {
        if (!isSelfViewTabId(id)) return;
        void navigate({ to: SELF_TAB_ROUTES[id] });
      }}
    >
      <Outlet />
    </UserRecordShell>
  );
}
